import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Link,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import ShieldMoonOutlinedIcon from "@mui/icons-material/ShieldMoonOutlined";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import { takedownApi } from "core/api";
import { UserRemoval } from "core/types/takedown.types";

type ChipColor = "default" | "success" | "warning" | "error" | "info";

// Indicizzata per stringa e non per UserRemoval["status"]: GET /account/removals legge
// signaled_urls.status, che e' varchar libero. Uno stato che il frontend non conosce ancora
// deve degradare nel ramo `else` (chip con lo slug grezzo), non far mancare la voce.
const STATUS_KEY: Record<string, { labelKey: string; color: ChipColor }> = {
  online: { labelKey: "takedown.removals.status.online", color: "error" },
  pending: { labelKey: "takedown.removals.status.pending", color: "warning" },
  removed: { labelKey: "takedown.common.status.removed", color: "success" },
  rejected: { labelKey: "takedown.removals.status.rejected", color: "default" },
  // NON e' 'removed': la pagina esiste ancora, semplicemente non e' piu' trovabile sui motori.
  // Dirle rimossa sarebbe smentito dal primo click del cliente sul link.
  deindexed: { labelKey: "takedown.removals.status.deindexed", color: "info" },
};

const CHANNEL_LABEL_KEY: Record<string, string> = {
  email_diffida: "takedown.removals.channel.email_diffida",
  pec: "takedown.removals.channel.pec",
  cloudflare: "takedown.removals.channel.cloudflare",
  host_abuse: "takedown.removals.channel.host_abuse",
  registrar_abuse: "takedown.removals.channel.registrar_abuse",
};

const fmtDate = (raw?: string | null): string => {
  if (!raw) return "—";
  const d = new Date(raw);
  if (isNaN(d.getTime())) return raw;
  return d.toLocaleDateString("it-IT", { year: "numeric", month: "long", day: "numeric" });
};

// Una rimozione DICHIARATA dal destinatario e non (ancora) verificata dalla sonda.
// Vedi la scelta di prodotto nel commento del componente.
const dichiarato = (r: UserRemoval): boolean => r.status_source === "dichiarazione";

/**
 * Vista end-user "Le tue rimozioni" — GET /account/removals (simple_auth, §5.3/§6.5).
 * Mostra, per ogni URL segnalato, lo stato (online/in lavorazione/rimosso) e il canale
 * che ha ottenuto la rimozione. Read-only. Da montare su una route utente (wiring: Integrator).
 *
 * SCELTA DI PRODOTTO sulle rimozioni DICHIARATE (migrazione 029), dichiarata qui perché è una
 * scelta e non un dettaglio implementativo.
 * Quando un operatore chiude una risposta registrando che il destinatario ha accolto, l'URL passa
 * a 'removed'/'deindexed' sulla PAROLA del destinatario: nessuno l'ha ancora verificato. Prima
 * quella riga era indistinguibile da una confermata dalla sonda, anche qui — cioè sul numero che
 * diciamo al cliente.
 * Si fanno DUE cose, e nessuna delle due è nascondere la riga:
 *   1. la si MOSTRA, con l'etichetta "dichiarato dal sito, in verifica": il cliente ha diritto di
 *      sapere che il sito ha risposto, ed è una buona notizia;
 *   2. NON la si CONTA fra le rimozioni confermate finché la sonda non conferma. Il chip verde
 *      "rimossi" resta il numero che possiamo dimostrare; le dichiarazioni hanno un contatore
 *      loro. Se la sonda conferma, la riga migra da sola nel conteggio verde (il reconcile
 *      riscrive status_source = 'sonda'); se smentisce, l'URL torna 'online' e non abbiamo mai
 *      dichiarato al cliente un risultato che non c'era.
 * L'alternativa — contarle subito — dà numeri più belli per qualche giorno e poi li toglie.
 */
export const UserRemovals: React.FC = () => {
  const { t } = useTranslation();
  const [rows, setRows] = useState<UserRemoval[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await takedownApi.getRemovals();
        if (active) setRows(Array.isArray(data) ? data : []);
      } catch (e: any) {
        if (active) setError(t("takedown.removals.error_load", { error: e?.message || e }));
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  // I chip non tornavano: si contavano solo 'removed' e 'pending', quindi un link deindicizzato
  // spariva da entrambi e la somma non arrivava mai al totale. 'deindexed' ha il suo conteggio.
  // `declared` è il terzo caso: rimosso/deindicizzato sulla parola del destinatario e non ancora
  // verificato dalla sonda. Non entra nei due conteggi sopra (vedi la scelta di prodotto in testa
  // al file): i numeri che diamo al cliente restano quelli che possiamo dimostrare.
  const stats = useMemo(() => {
    const confermate = rows.filter((r) => !dichiarato(r));
    const removed = confermate.filter((r) => r.status === "removed").length;
    const deindexed = confermate.filter((r) => r.status === "deindexed").length;
    const declared = rows.filter(
      (r) => dichiarato(r) && (r.status === "removed" || r.status === "deindexed")
    ).length;
    const pending = rows.filter((r) => r.status === "pending").length;
    return { removed, deindexed, declared, pending, total: rows.length };
  }, [rows]);

  return (
    <Box sx={{ p: 3, maxWidth: 1000, mx: "auto" }}>
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 0.5 }}>
        <ShieldMoonOutlinedIcon color="primary" />
        <Typography variant="h5" fontWeight={700}>
          {t("takedown.removals.title")}
        </Typography>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t("takedown.removals.description")}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {!loading && rows.length > 0 && (
        <Stack direction="row" spacing={1.5} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
          <Chip color="success" label={t("takedown.removals.stat_removed", { count: stats.removed })} />
          {stats.deindexed > 0 && (
            <Chip
              color="info"
              label={t("takedown.removals.stat_deindexed", {
                count: stats.deindexed,
              })}
            />
          )}
          {stats.declared > 0 && (
            <Chip
              color="warning"
              variant="outlined"
              label={t("takedown.removals.stat_declared", { count: stats.declared })}
            />
          )}
          <Chip color="warning" label={t("takedown.removals.stat_pending", { count: stats.pending })} />
          <Chip variant="outlined" label={t("takedown.removals.stat_total", { count: stats.total })} />
        </Stack>
      )}

      <Paper sx={{ borderRadius: "10px", p: "12px" }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}>
            <CircularProgress size={56} color="secondary" />
          </Box>
        ) : (
          <TableContainer sx={{ maxHeight: 560 }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>{t("takedown.removals.table.content")}</TableCell>
                  <TableCell>{t("takedown.removals.table.status")}</TableCell>
                  <TableCell>{t("takedown.removals.table.channel")}</TableCell>
                  <TableCell>{t("takedown.removals.table.removed_at")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody sx={{ "& .MuiTableCell-root": { padding: "10px", fontSize: "14px" } }}>
                {rows.map((r, i) => {
                  const smMeta = STATUS_KEY[r.status];
                  const sm = smMeta
                    ? { label: t(smMeta.labelKey), color: smMeta.color }
                    : { label: r.status, color: "default" as ChipColor };
                  // Dichiarata dal destinatario e non ancora verificata: l'etichetta lo dice, e il
                  // colore smette di essere quello del risultato acquisito.
                  const sd = dichiarato(r) && (r.status === "removed" || r.status === "deindexed");
                  const chip = sd
                    ? { label: t("takedown.removals.status.declared"), color: "warning" as ChipColor }
                    : sm;
                  return (
                    <TableRow key={`${r.url}-${i}`} hover>
                      <TableCell sx={{ maxWidth: 420 }}>
                        <Link
                          href={r.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          underline="hover"
                          sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, wordBreak: "break-all" }}
                        >
                          {r.domain || r.url}
                          <OpenInNewRoundedIcon fontSize="inherit" />
                        </Link>
                      </TableCell>
                      <TableCell>
                        <Tooltip title={sd ? t("takedown.removals.status.declared_hint") : ""}>
                          <Chip size="small" color={chip.color} label={chip.label} />
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        {r.channel ? (CHANNEL_LABEL_KEY[r.channel] ? t(CHANNEL_LABEL_KEY[r.channel]) : r.channel) : "—"}
                      </TableCell>
                      <TableCell>{fmtDate(r.removed_at)}</TableCell>
                    </TableRow>
                  );
                })}
                {!rows.length && (
                  <TableRow>
                    <TableCell colSpan={4}>
                      <Typography color="text.secondary" sx={{ py: 3, textAlign: "center" }}>
                        {t("takedown.removals.empty")}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </Box>
  );
};

export default UserRemovals;
